/**
 * Webhooks Service
 * API calls for webhook management
 */

import apiClient from './api.client';
import type { ApiResponse, WebhookEndpoint, CreateWebhookRequest, WebhookLog } from '@/types/api.types';

/**
 * List all webhooks (admin)
 */
export const listWebhooks = async (): Promise<WebhookEndpoint[]> => {
  const response = await apiClient.get<ApiResponse<{ webhooks: WebhookEndpoint[] }>>('/admin/webhooks');
  if (!response.data.data) throw new Error(response.data.error || 'Failed to fetch webhooks');
  return response.data.data.webhooks;
};

/**
 * Create a webhook (admin) — returns secret on creation only
 */
export const createWebhook = async (data: CreateWebhookRequest): Promise<WebhookEndpoint> => {
  const response = await apiClient.post<ApiResponse<{ webhook: WebhookEndpoint }>>('/admin/webhooks', data);
  if (!response.data.data) throw new Error(response.data.error || 'Failed to create webhook');
  return response.data.data.webhook;
};

/**
 * Update a webhook (admin)
 */
export const updateWebhook = async (webhookId: string, data: Partial<CreateWebhookRequest>): Promise<WebhookEndpoint> => {
  const response = await apiClient.patch<ApiResponse<{ webhook: WebhookEndpoint }>>(`/admin/webhooks/${webhookId}`, data);
  if (!response.data.data) throw new Error(response.data.error || 'Failed to update webhook');
  return response.data.data.webhook;
};

/**
 * Delete a webhook (admin)
 */
export const deleteWebhook = async (webhookId: string): Promise<void> => {
  await apiClient.delete(`/admin/webhooks/${webhookId}`);
};

/**
 * Get webhook logs (admin)
 */
export const getWebhookLogs = async (webhookId: string, limit = 50): Promise<WebhookLog[]> => {
  const response = await apiClient.get<ApiResponse<{ logs: WebhookLog[] }>>(`/admin/webhooks/${webhookId}/logs`, { params: { limit } });
  if (!response.data.data) throw new Error(response.data.error || 'Failed to fetch webhook logs');
  return response.data.data.logs;
};
